import { useState, useEffect } from 'react';
import { useTechnicians } from '../contexts/TechniciansContext';
import { useSkills } from '../contexts/SkillsContext';
import { useOnCalls } from '../contexts/OnCallsContext';
import { useAbsences } from '../contexts/AbsencesContext';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';

export const useGlobalLoading = () => {
  const [isLoadingIndicatorOpen, setIsLoadingIndicatorOpen] = useState(false);
  const [loadingStartTime, setLoadingStartTime] = useState(null);
  const [teamChangeTime, setTeamChangeTime] = useState(null);
  const [minDisplayTime] = useState(1000); // Temps minimum d'affichage: 1 seconde
  
  const { currentUser } = useAuth();
  const { selectedTeam } = useTeam();
  
  const { 
    technicians, 
    loading: techniciansLoading, 
    error: techniciansError 
  } = useTechnicians();
  
  const { 
    skills, 
    loading: skillsLoading, 
    error: skillsError 
  } = useSkills();
  
  const { 
    onCalls, 
    loading: onCallsLoading, 
    error: onCallsError 
  } = useOnCalls();
  
  const { 
    absences, 
    loading: absencesLoading, 
    error: absencesError 
  } = useAbsences();

  // État de chargement pour chaque context
  const loadingStates = {
    technicians: {
      loading: techniciansLoading,
      loaded: !techniciansLoading && !techniciansError && technicians.length >= 0,
      count: techniciansLoading ? 0 : (technicians?.length || 0), // Reset count pendant chargement
      error: techniciansError
    },
    skills: {
      loading: skillsLoading,
      loaded: !skillsLoading && !skillsError && skills.length >= 0,
      count: skillsLoading ? 0 : (skills?.length || 0), // Reset count pendant chargement
      error: skillsError
    },
    oncalls: {
      loading: onCallsLoading,
      loaded: !onCallsLoading && !onCallsError && onCalls.length >= 0,
      count: onCallsLoading ? 0 : (onCalls?.length || 0), // Reset count pendant chargement
      error: onCallsError
    },
    absences: {
      loading: absencesLoading,
      loaded: !absencesLoading && !absencesError && absences.length >= 0,
      count: absencesLoading ? 0 : (absences?.length || 0), // Reset count pendant chargement
      error: absencesError,
      progress: absencesLoading ? 'Traitement en cours...' : null
    }
  };

  const isAnyLoading = Object.values(loadingStates).some(state => state.loading);
  const allLoaded = Object.values(loadingStates).every(state => state.loaded);
  const hasErrors = Object.values(loadingStates).some(state => state.error);

  // Détecter les changements d'équipe
  useEffect(() => {
    if (currentUser && selectedTeam) {
      setTeamChangeTime(Date.now());
      console.log('📅 Équipe sélectionnée:', selectedTeam?.name, 'Loading states:', Object.entries(loadingStates).map(([key, state]) => `${key}:${state.loading}`).join(', '));
    }
  }, [currentUser, selectedTeam?.id]);

  // Détecter le début d'un chargement - attendre que les contextes commencent vraiment
  useEffect(() => {
    if (currentUser && selectedTeam && isAnyLoading) {
      const loadingContexts = Object.entries(loadingStates).filter(([_, state]) => state.loading).map(([key]) => key);
      console.log('🔄 Début détecté du chargement global pour équipe:', selectedTeam?.name, 'contexts loading:', loadingContexts);
      
      // Si ce n'est pas déjà en cours, démarrer le chargement
      if (!loadingStartTime) {
        setLoadingStartTime(Date.now());
        setIsLoadingIndicatorOpen(true);
      }
    }
  }, [currentUser, selectedTeam, isAnyLoading, loadingStartTime]);

  // Détecter la fin du chargement
  useEffect(() => {
    if (loadingStartTime && !isAnyLoading && (allLoaded || hasErrors)) {
      const loadingDuration = Date.now() - loadingStartTime;
      console.log(`✅ Chargement global terminé en ${loadingDuration}ms`);
      
      // S'assurer d'un temps minimum d'affichage pour une meilleure UX
      const timeToWait = Math.max(0, minDisplayTime - loadingDuration);
      
      if (allLoaded) {
        setTimeout(() => {
          setIsLoadingIndicatorOpen(false);
          setLoadingStartTime(null);
        }, timeToWait);
      } else {
        // Fermer avec délai minimum même en cas d'erreur
        setTimeout(() => {
          setIsLoadingIndicatorOpen(false);
          setLoadingStartTime(null);
        }, Math.min(timeToWait, 500));
      }
    }
  }, [isAnyLoading, allLoaded, hasErrors, loadingStartTime, minDisplayTime]);

  // Réinitialiser seulement quand l'utilisateur change (pas l'équipe)
  useEffect(() => {
    if (!currentUser?.id) {
      setLoadingStartTime(null);
      setIsLoadingIndicatorOpen(false);
    }
  }, [currentUser?.id]);

  // Fermeture manuelle de l'indicateur
  const closeLoadingIndicator = () => {
    setIsLoadingIndicatorOpen(false);
    setLoadingStartTime(null);
  };

  // Statistiques de chargement
  const loadingStats = {
    totalLoaded: Object.values(loadingStates).filter(state => state.loaded).length,
    totalContexts: Object.keys(loadingStates).length,
    isAnyLoading,
    allLoaded,
    hasErrors,
    loadingDuration: loadingStartTime ? Date.now() - loadingStartTime : 0
  };

  return {
    isLoadingIndicatorOpen,
    loadingStates,
    loadingStats,
    closeLoadingIndicator
  };
};